import clsx from "clsx";
import { FC, useState } from "react";

import { EvalResults, EvalSpec } from "@tsmono/inspect-common/types";
import { Modal } from "@tsmono/react/components";
import { Icon, IconName } from "@tsmono/react/icons";
import { formatPrettyDecimal } from "@tsmono/util";

import { EvalLogStatus } from "../../../@types/extraInspect";
import { RunningMetric } from "../../../client/api/types";
import { LinkButton } from "../../../components/LinkButton";
import { leadWith, resolveHeadlineMetric } from "../../../scoring/headline";
import {
  expandGroupedMetrics,
  metricDisplayName,
  toDisplayScorers,
} from "../../../scoring/metrics";
import { groupScorers } from "../../../scoring/scores";
import { useEffectiveEvalConfig } from "../../../state/hooks";
import { formatModelTitle } from "../../../utils/evalModel";

import styles from "./CollapsedTitleBar.module.css";
import { displayScorersFromRunningMetrics } from "./ResultsPanel";
import { ScoreGrid } from "./ScoreGrid";

const kInlineMetricLimit = 2;

interface CollapsedTitleBarProps {
  evalSpec?: EvalSpec;
  evalResults?: EvalResults | null;
  runningMetrics?: RunningMetric[];
  status?: EvalLogStatus;
  sampleCount?: number;
}

export const CollapsedTitleBar: FC<CollapsedTitleBarProps> = ({
  evalSpec,
  evalResults,
  runningMetrics,
  status,
  sampleCount,
}) => {
  // Effective (folded) config: a mid-run change to continue_on_fail decides
  // whether an errored run still shows metrics.
  const effectiveConfig = useEffectiveEvalConfig();
  const showMetrics =
    status === "success" ||
    (status === "started" && (runningMetrics?.length ?? 0) > 0) ||
    (status === "error" && effectiveConfig?.continue_on_fail);

  const scorers = runningMetrics
    ? displayScorersFromRunningMetrics(runningMetrics)
    : toDisplayScorers(
        evalResults?.scores,
        resolveHeadlineMetric(evalResults, evalSpec?.headline_metric)
      );

  const expandedScorers = expandGroupedMetrics(scorers);
  const totalMetrics = expandedScorers.reduce(
    (n, s) => n + s.metrics.length,
    0
  );

  const modelText = formatModelTitle(evalSpec);

  return (
    <div
      className={clsx("navbar-brand", "navbar-text", "mb-0", styles.container)}
    >
      <div className={clsx(styles.left)}>
        <span
          id="task-title-collapsed"
          className={clsx(
            "task-title",
            "text-truncate",
            "text-size-larger",
            styles.task
          )}
          title={evalSpec?.task}
        >
          {evalSpec?.task}
        </span>
        {modelText ? (
          <span
            id="task-model-collapsed"
            className={clsx(
              "task-model",
              "text-truncate",
              "text-size-small",
              styles.model
            )}
            title={modelText}
          >
            {modelText}
          </span>
        ) : null}
      </div>
      <div className={clsx(styles.right, "text-size-smaller")}>
        {showMetrics && totalMetrics > 0 ? (
          totalMetrics <= kInlineMetricLimit ? (
            <InlineMetrics scorers={expandedScorers} />
          ) : (
            <MetricsLink scorers={expandedScorers} />
          )
        ) : (
          <StatusBadge status={status} sampleCount={sampleCount} />
        )}
      </div>
    </div>
  );
};

interface InlineMetricsProps {
  scorers: ReturnType<typeof expandGroupedMetrics>;
}

const InlineMetrics: FC<InlineMetricsProps> = ({ scorers }) => {
  const items: {
    key: string;
    label: string;
    value: string;
    headline: boolean;
  }[] = [];
  scorers.forEach((scorer, scorerIdx) => {
    scorer.metrics.forEach((metric, metricIdx) => {
      items.push({
        key: `${scorerIdx}-${metricIdx}`,
        label: metricDisplayName(metric),
        value:
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          metric.value !== undefined && metric.value !== null
            ? formatPrettyDecimal(metric.value)
            : "n/a",
        headline: metric.headline === true,
      });
    });
  });
  // collapsing shows only a few metrics inline, so lead with the headline
  const ordered = leadWith(
    items,
    items.findIndex((item) => item.headline)
  );

  return (
    <div className={styles.inlineMetrics}>
      {ordered.map((item) => (
        <span key={item.key} className={styles.inlineMetric}>
          <span className={clsx("text-style-label", styles.inlineMetricLabel)}>
            {item.label}
          </span>
          <span className={styles.inlineMetricValue}>{item.value}</span>
        </span>
      ))}
    </div>
  );
};

interface StatusBadgeProps {
  status?: EvalLogStatus;
  sampleCount?: number;
}

const StatusBadge: FC<StatusBadgeProps> = ({ status, sampleCount }) => {
  const display = statusDisplay(status);
  if (!display) return null;
  const count = sampleCount ?? 0;
  return (
    <div className={styles.statusBadge}>
      <Icon name={display.icon} className={styles.statusIcon} />
      <span className="text-style-label">{display.label}</span>
      {count > 0 ? (
        <span className={styles.statusCount}>
          ({count} {count === 1 ? "sample" : "samples"})
        </span>
      ) : null}
    </div>
  );
};

const statusDisplay = (
  status?: EvalLogStatus
): { icon: IconName; label: string } | undefined => {
  switch (status) {
    case "started":
      return { icon: "running", label: "Running" };
    case "cancelled":
      return { icon: "logInfo", label: "Cancelled" };
    case "error":
      return { icon: "logError", label: "Task Failed" };
    default:
      return undefined;
  }
};

interface MetricsLinkProps {
  scorers: ReturnType<typeof expandGroupedMetrics>;
}

const MetricsLink: FC<MetricsLinkProps> = ({ scorers }) => {
  const grouped = groupScorers(scorers);
  const showReducer = scorers.findIndex((s) => !!s.reducer) !== -1;

  // Modal portals to <body> internally, so it escapes the title-view
  // collapsing wrapper (which sets opacity: 0 on whichever slot is hidden)
  // without a manual portal here.
  const [showing, setShowing] = useState(false);

  return (
    <>
      <LinkButton text="View metrics…" onClick={() => setShowing(true)} />
      <Modal
        id="collapsed-title-bar-metrics"
        show={showing}
        onHide={() => setShowing(false)}
        title="Scoring Detail"
        width="min(1000px, 90vw)"
        overflow="hidden"
        padded={false}
        className={styles.scoringDetailModal}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowing(false)}
          >
            Close
          </button>
        }
      >
        <ScoreGrid scoreGroups={grouped} showReducer={showReducer} />
      </Modal>
    </>
  );
};
